import React, { useContext, useEffect, useMemo, useRef } from 'react';

import {
  LinearProgress,
  Button,
  MenuItem,
  InputLabel,
  FormControlLabel,
} from '@material-ui/core';
import { Formik, Form, Field, FormikHelpers, FormikProps } from 'formik';
import { Switch, TextField } from 'formik-material-ui';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { getOrganizations } from 'actions';
import { CardTitle } from 'app/components/shared/card-title';
import { getInputProps } from 'app/components/shared/groups-menu-multi-select/utils';
import { DIRECTIONS } from 'app/i18n-locale/locales-constants';
import {
  StationFormState,
  StationFormSchema,
} from 'app/operations/stations/station-helpers';
import { LanguageContext } from 'context/intl.context';
import { getTranslated, useTypedSelector } from 'helpers';
import { getOrganizationsWithWritePermission } from 'selectors';
import { Organization } from 'types';

import { useStyles } from '../drivers/driver-form';

interface StationForm {
  initialState: StationFormState;
  handleSubmit: (
    values: StationFormState,
    { setSubmitting }: FormikHelpers<StationFormState>
  ) => void;
  isEditForm?: boolean;
}

const StationForm: React.FC<StationForm> = ({
  initialState,
  handleSubmit,
  isEditForm = false,
}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const classes = useStyles();
  const intl = useIntl();
  const { direction, locale } = useContext(LanguageContext);

  const isRTL = direction === DIRECTIONS.RTL;

  const organizationList = useTypedSelector(state =>
    getOrganizationsWithWritePermission(state, 'Stations')
  ) as Organization[];

  const hasWriteAccessOnOrganizations = organizationList?.length > 0;

  const organizations = useMemo(() => {
    if (hasWriteAccessOnOrganizations) return organizationList;
    if (initialState.organisation) return [initialState.organisation];

    return null;
  }, [initialState, organizationList, hasWriteAccessOnOrganizations]);

  const formikRef = useRef<FormikProps<StationFormState>>(null);

  useEffect(() => {
    dispatch(getOrganizations(0, 100));
  }, [dispatch]);

  useEffect(() => {
    formikRef?.current?.validateForm();
  }, [formikRef, direction]);

  return (
    <>
      <CardTitle
        title={intl.formatMessage({
          id: isEditForm ? 'editStation' : 'addStation',
        })}
      />

      <div className={classes.formWrapper}>
        <Formik
          innerRef={formikRef}
          initialValues={initialState}
          validationSchema={() => StationFormSchema(intl)}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ isSubmitting }) => (
            <Form>
              <div className={classes.fieldGroupWrapper}>
                <div className={classes.fieldWrapper}>
                  <InputLabel
                    className={classes.formLabel}
                    style={{ flexDirection: 'row-reverse' }}
                  >
                    <strong className={classes.requiredAstrisk}>*</strong>

                    <span>{intl.formatMessage({ id: 'englishName' })}</span>
                  </InputLabel>

                  <Field
                    name="en_name"
                    component={TextField}
                    type="text"
                    variant="outlined"
                    size="small"
                  />
                </div>

                <div className={classes.fieldWrapper}>
                  <InputLabel
                    className={classes.formLabel}
                    style={{ flexDirection: 'row-reverse' }}
                  >
                    <strong className={classes.requiredAstrisk}>*</strong>

                    <span>{intl.formatMessage({ id: 'arabicName' })}</span>
                  </InputLabel>

                  <Field
                    name="ar_name"
                    component={TextField}
                    type="text"
                    variant="outlined"
                    size="small"
                  />
                </div>

                <div className={classes.fieldWrapper}>
                  <InputLabel
                    className={classes.formLabel}
                    style={{ flexDirection: 'row-reverse' }}
                  >
                    <strong className={classes.requiredAstrisk}>*</strong>

                    <span>{intl.formatMessage({ id: 'stationCode' })}</span>
                  </InputLabel>

                  <Field
                    name="station_code"
                    component={TextField}
                    type="text"
                    variant="outlined"
                    size="small"
                    placeholder="e.g. ABC123"
                  />
                </div>

                <div className={classes.fieldWrapper}>
                  <InputLabel className={classes.formLabel}>
                    {intl.formatMessage({ id: 'organization' })}

                    <strong className={classes.requiredAstrisk}>*</strong>
                  </InputLabel>

                  <Field
                    name="organisationId"
                    component={TextField}
                    type="text"
                    select
                    variant="outlined"
                    size="small"
                    inputProps={getInputProps(isRTL)}
                  >
                    {organizations?.map(organization => (
                      <MenuItem
                        key={organization.id}
                        value={organization.id}
                        className={classes.selectMenuItem}
                      >
                        {getTranslated(organization, locale)}
                      </MenuItem>
                    ))}
                  </Field>
                </div>

                <div className={classes.fieldWrapper} style={{ width: '100%' }}>
                  <InputLabel className={classes.formLabel}>
                    {intl.formatMessage({ id: 'status' })}
                  </InputLabel>

                  <FormControlLabel
                    control={
                      <Field
                        name="enabled"
                        type="checkbox"
                        component={Switch}
                        color="Secondary"
                      />
                    }
                    label={intl.formatMessage({
                      id: 'enabled',
                    })}
                  />
                </div>
              </div>

              {isSubmitting && <LinearProgress />}

              <div className={classes.buttonsWrapper}>
                <Button
                  variant="outlined"
                  type="submit"
                  disabled={isSubmitting}
                  className={`${classes.submitButton} ${classes.actionButton}`}
                >
                  {intl.formatMessage({
                    id: isEditForm ? 'update' : 'addStation',
                  })}
                </Button>

                <Button
                  variant="outlined"
                  color="default"
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => history.go(-1)}
                  className={classes.actionButton}
                >
                  {intl.formatMessage({
                    id: 'cancel',
                  })}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </>
  );
};

export { StationForm };
